/**
 * As três caixas que registram uma decisão do funil: aprovação, compra e
 * motivo da recusa.
 *
 * Papel: cada caixa pergunta uma coisa só — quem aprovou, quem comprou, ou por
 * que o pedido foi reprovado — e devolve a resposta a quem a abriu, sem gravar
 * nada. Quem grava é quem monta: `PaginaCompras` grava a transição na hora;
 * `FormularioPedido` só muda os campos na tela, e a gravação espera o "Salvar
 * pedido de compra".
 *
 * Como usar: as três são controladas por `aberta`, e a resposta digitada vive
 * dentro do conteúdo da caixa — que só existe enquanto ela está aberta —, e por
 * isso toda abertura começa em branco, sem precisar de `key` nem de limpeza por
 * quem monta. Uma gravação que falha deverá chegar de volta por `erro`, com a
 * caixa ainda aberta: fechar antes de a gravação responder esconderia a falha
 * atrás de uma tela onde nada mudou.
 *
 * Quem aprovou e quem comprou são texto, e não escolha fechada: os `Chip` de
 * `APROVADORES_SUGERIDOS` e `COMPRADORES_SUGERIDOS` preenchem o campo com um
 * clique, e o campo aceita qualquer outro nome. As três caixas são
 * `AlertDialog`, que não fecham por clique no véu — uma decisão só sai pelo
 * botão que a confirma ou pelo "Cancelar".
 *
 * `quantidade` acima de um troca a pergunta pela do lote — "N pedidos
 * selecionados…" —, e a resposta devolvida vale para todos eles; quem monta
 * grava cada pedido com ela.
 */
import { APROVADORES_SUGERIDOS, COMPRADORES_SUGERIDOS } from '@concierge/shared';
import { useId, useState } from 'react';

import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/shared/ui/alert-dialog';
import { Button } from '@/shared/ui/button';
import { Chip } from '@/shared/ui/chip';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';
import { Textarea } from '@/shared/ui/textarea';

/** O que as três caixas recebem de quem as monta. */
interface CaixaDeDecisaoProps {
  /** Se a caixa está à mostra. */
  aberta: boolean;
  /** Desabilita os botões enquanto a gravação que a resposta disparou não volta. */
  ocupada: boolean;
  /** A falha da última gravação, mostrada dentro da caixa; `null` sem falha. */
  erro: string | null;
  /** Chamado ao cancelar, pelo botão ou pela tecla Esc. */
  onCancelar: () => void;
  /** Quantos pedidos a decisão alcança; sem valor, um. */
  quantidade?: number;
}

/** A falha da gravação, dentro da caixa, onde a pessoa está olhando. */
function ErroDaCaixa({ erro }: { erro: string | null }) {
  if (erro === null) return null;
  return (
    <p role="alert" className="my-3 text-sm text-perigo">
      {erro}
    </p>
  );
}

/**
 * O campo de quem decidiu, com as sugestões em `Chip` acima dele.
 *
 * @param rotulo - O rótulo do campo, em caixa alta.
 * @param sugestoes - Os nomes oferecidos em `Chip`; clicar num preenche o campo.
 * @param valor - O que o campo mostra agora.
 * @param onMudar - Chamado com o nome escolhido ou digitado.
 * @param desabilitado - Trava sugestões e campo durante a gravação.
 * @returns O rótulo, a fileira de sugestões e o campo livre.
 */
function CampoDeQuemDecidiu({
  rotulo,
  sugestoes,
  valor,
  onMudar,
  desabilitado,
}: {
  rotulo: string;
  sugestoes: readonly string[];
  valor: string;
  onMudar: (nome: string) => void;
  desabilitado: boolean;
}) {
  const id = useId();

  return (
    <div className="mb-5">
      <Label htmlFor={id}>{rotulo}</Label>
      <div className="mt-4 flex flex-wrap gap-2.5">
        {sugestoes.map((nome) => (
          <Chip
            key={nome}
            ativo={valor === nome}
            onClick={() => onMudar(nome)}
            disabled={desabilitado}
          >
            {nome}
          </Chip>
        ))}
      </div>
      <Input
        id={id}
        value={valor}
        placeholder="Ou digite outro nome"
        onChange={(evento) => onMudar(evento.target.value)}
        disabled={desabilitado}
        className="mt-2.5"
      />
    </div>
  );
}

/** O que a caixa de aprovação acrescenta às props comuns. */
interface CaixaDeAprovacaoProps extends CaixaDeDecisaoProps {
  /** Chamado com quem aprovou, já sem espaços nas pontas. */
  onConfirmar: (aprovador: string) => void;
  /** Chamado em "✕ Reprovar"; quem monta fecha esta caixa e abre a do motivo. */
  onReprovar: () => void;
}

/**
 * A caixa "Decisão de aprovação".
 *
 * @param aberta - Se a caixa está à mostra.
 * @param ocupada - Desabilita os botões durante a gravação.
 * @param erro - A falha da última gravação, ou `null`.
 * @param onConfirmar - Chamado com o aprovador; o botão fica desabilitado
 *   enquanto não houver nome.
 * @param onReprovar - Chamado em "✕ Reprovar", que não pede aprovador.
 * @param onCancelar - Chamado ao desistir.
 * @param quantidade - Quantos pedidos a decisão alcança; acima de um, a
 *   pergunta é a do lote.
 * @returns A caixa, ou nada enquanto `aberta` for falso.
 */
export function CaixaDeAprovacao({
  aberta,
  ocupada,
  erro,
  onConfirmar,
  onReprovar,
  onCancelar,
  quantidade = 1,
}: CaixaDeAprovacaoProps) {
  return (
    <AlertDialog open={aberta} onOpenChange={(aberto) => !aberto && onCancelar()}>
      <AlertDialogContent>
        <ConteudoDaAprovacao
          ocupada={ocupada}
          erro={erro}
          onConfirmar={onConfirmar}
          onReprovar={onReprovar}
          quantidade={quantidade}
        />
      </AlertDialogContent>
    </AlertDialog>
  );
}

/** O miolo da caixa de aprovação, que nasce em branco a cada abertura. */
function ConteudoDaAprovacao({
  ocupada,
  erro,
  onConfirmar,
  onReprovar,
  quantidade = 1,
}: Omit<CaixaDeAprovacaoProps, 'aberta' | 'onCancelar'>) {
  const [aprovador, setAprovador] = useState('');

  return (
    <>
      <AlertDialogHeader className="mb-2">
        <AlertDialogTitle>Decisão de aprovação</AlertDialogTitle>
        <AlertDialogDescription>
          {quantidade > 1
            ? `${String(quantidade)} pedidos selecionados foram aprovados ou reprovados? Se aprovados, selecione quem aprovou — a decisão vale pra todos de uma vez, e a data fica registrada automaticamente como hoje.`
            : 'Este pedido foi aprovado ou reprovado? Se aprovado, selecione quem aprovou — a data fica registrada automaticamente como hoje.'}
        </AlertDialogDescription>
      </AlertDialogHeader>

      <CampoDeQuemDecidiu
        rotulo="APROVADOR"
        sugestoes={APROVADORES_SUGERIDOS}
        valor={aprovador}
        onMudar={setAprovador}
        desabilitado={ocupada}
      />

      <ErroDaCaixa erro={erro} />

      <div className="mt-1.5">
        <Button
          className="w-full"
          onClick={() => onConfirmar(aprovador.trim())}
          disabled={ocupada || aprovador.trim() === ''}
        >
          ✓ Confirmar aprovação
        </Button>
      </div>
      <div className="mt-3.5 border-t border-linha-forte pt-3.5">
        <Button
          variant="destructive"
          className="w-full"
          onClick={onReprovar}
          disabled={ocupada}
        >
          ✕ Reprovar
        </Button>
      </div>

      <AlertDialogFooter>
        <AlertDialogCancel disabled={ocupada}>Cancelar</AlertDialogCancel>
      </AlertDialogFooter>
    </>
  );
}

/** O que a caixa de compra acrescenta às props comuns. */
interface CaixaDeCompraProps extends CaixaDeDecisaoProps {
  /** Chamado com quem comprou, já sem espaços nas pontas. */
  onConfirmar: (compradoPor: string) => void;
}

/**
 * A caixa "Registrar compra".
 *
 * @param aberta - Se a caixa está à mostra.
 * @param ocupada - Desabilita os botões durante a gravação.
 * @param erro - A falha da última gravação, ou `null`.
 * @param onConfirmar - Chamado com quem comprou; o botão fica desabilitado
 *   enquanto não houver nome.
 * @param onCancelar - Chamado ao desistir.
 * @param quantidade - Quantos pedidos a decisão alcança; acima de um, a
 *   pergunta é a do lote.
 * @returns A caixa, ou nada enquanto `aberta` for falso.
 */
export function CaixaDeCompra({
  aberta,
  ocupada,
  erro,
  onConfirmar,
  onCancelar,
  quantidade = 1,
}: CaixaDeCompraProps) {
  return (
    <AlertDialog open={aberta} onOpenChange={(aberto) => !aberto && onCancelar()}>
      <AlertDialogContent>
        <ConteudoDaCompra
          ocupada={ocupada}
          erro={erro}
          onConfirmar={onConfirmar}
          quantidade={quantidade}
        />
      </AlertDialogContent>
    </AlertDialog>
  );
}

/** O miolo da caixa de compra, que nasce em branco a cada abertura. */
function ConteudoDaCompra({
  ocupada,
  erro,
  onConfirmar,
  quantidade = 1,
}: Omit<CaixaDeCompraProps, 'aberta' | 'onCancelar'>) {
  const [compradoPor, setCompradoPor] = useState('');

  return (
    <>
      <AlertDialogHeader className="mb-2">
        <AlertDialogTitle>Registrar compra</AlertDialogTitle>
        <AlertDialogDescription>
          {quantidade > 1
            ? `Quem fez a compra dos ${String(quantidade)} pedidos selecionados? A resposta vale pra todos de uma vez.`
            : 'Quem fez a compra deste pedido?'}
        </AlertDialogDescription>
      </AlertDialogHeader>

      <CampoDeQuemDecidiu
        rotulo="QUEM COMPROU"
        sugestoes={COMPRADORES_SUGERIDOS}
        valor={compradoPor}
        onMudar={setCompradoPor}
        desabilitado={ocupada}
      />

      <ErroDaCaixa erro={erro} />

      <div className="mt-1.5">
        <Button
          className="w-full"
          onClick={() => onConfirmar(compradoPor.trim())}
          disabled={ocupada || compradoPor.trim() === ''}
        >
          ✓ Confirmar compra
        </Button>
      </div>

      <AlertDialogFooter>
        <AlertDialogCancel disabled={ocupada}>Cancelar</AlertDialogCancel>
      </AlertDialogFooter>
    </>
  );
}

/**
 * O motivo entregue quando a reprovação é confirmada com o campo em branco.
 *
 * Um pedido finalizado com `motivoReprovacao` nulo passaria por concluído na
 * tabela de finalizados, e por isso a recusa sem motivo ainda grava um texto.
 */
const MOTIVO_EM_BRANCO = 'Sem motivo registrado.';

/** O que a caixa do motivo acrescenta às props comuns. */
interface CaixaDeReprovacaoProps extends CaixaDeDecisaoProps {
  /**
   * Chamado com o motivo, já sem espaços nas pontas; o campo em branco chega
   * como `MOTIVO_EM_BRANCO`, nunca como texto vazio.
   */
  onConfirmar: (motivo: string) => void;
}

/**
 * A caixa "Motivo da recusa", que encerra o pedido como reprovado.
 *
 * @param aberta - Se a caixa está à mostra.
 * @param ocupada - Desabilita os botões durante a gravação.
 * @param erro - A falha da última gravação, ou `null`.
 * @param onConfirmar - Chamado com o motivo digitado, ou com o texto de motivo
 *   em branco quando nada foi digitado.
 * @param onCancelar - Chamado ao desistir, sem reprovar nada.
 * @param quantidade - Quantos pedidos a recusa alcança; acima de um, o rótulo
 *   do campo é o do lote.
 * @returns A caixa, ou nada enquanto `aberta` for falso.
 */
export function CaixaDeReprovacao({
  aberta,
  ocupada,
  erro,
  onConfirmar,
  onCancelar,
  quantidade = 1,
}: CaixaDeReprovacaoProps) {
  return (
    <AlertDialog open={aberta} onOpenChange={(aberto) => !aberto && onCancelar()}>
      <AlertDialogContent aria-describedby={undefined}>
        <ConteudoDaReprovacao
          ocupada={ocupada}
          erro={erro}
          onConfirmar={onConfirmar}
          quantidade={quantidade}
        />
      </AlertDialogContent>
    </AlertDialog>
  );
}

/** O miolo da caixa do motivo, que nasce em branco a cada abertura. */
function ConteudoDaReprovacao({
  ocupada,
  erro,
  onConfirmar,
  quantidade = 1,
}: Omit<CaixaDeReprovacaoProps, 'aberta' | 'onCancelar'>) {
  const [motivo, setMotivo] = useState('');
  const id = useId();

  return (
    <>
      <AlertDialogHeader>
        <AlertDialogTitle>Motivo da recusa</AlertDialogTitle>
      </AlertDialogHeader>

      <div className="flex flex-col gap-1.5">
        <Label htmlFor={id}>
          {quantidade > 1
            ? `POR QUE OS ${String(quantidade)} PEDIDOS SELECIONADOS FORAM REPROVADOS?`
            : 'POR QUE O PEDIDO FOI REPROVADO?'}
        </Label>
        <Textarea
          id={id}
          rows={4}
          placeholder="Ex: fora do orçamento, item duplicado, fornecedor não aprovado..."
          value={motivo}
          onChange={(evento) => setMotivo(evento.target.value)}
          disabled={ocupada}
          className="min-h-24"
        />
      </div>

      <ErroDaCaixa erro={erro} />

      <AlertDialogFooter>
        <AlertDialogCancel disabled={ocupada}>Cancelar</AlertDialogCancel>
        <Button
          variant="destructive"
          onClick={() => onConfirmar(motivo.trim() === '' ? MOTIVO_EM_BRANCO : motivo.trim())}
          disabled={ocupada}
        >
          Reprovar e finalizar pedido
        </Button>
      </AlertDialogFooter>
    </>
  );
}
